/**
 * AlertSettings modal — webhook config, sound toggles, notification permission.
 * Slides in from the right as a full-height drawer.
 */

import { memo, useState } from 'react';
import { useTerminalStore, selectAlertSettings } from '../../stores/terminal.store.js';
import { testBuySound, testSellSound } from '../../hooks/useAlerts.js';

// ─── Toggle Switch ────────────────────────────────────────────────────────────

function Toggle({ value, onChange, label }: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="font-mono text-[11px] text-muted">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-8 h-4 border transition-colors ${
          value ? 'border-arc bg-[rgba(0,191,255,0.2)]' : 'border-hud bg-arc-dim'
        }`}
      >
        <span
          className={`absolute top-0.5 w-2.5 h-2.5 transition-all ${
            value ? 'left-[18px] bg-arc' : 'left-0.5 bg-[var(--color-border)]'
          }`}
        />
      </button>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHead({ label }: { label: string }) {
  return (
    <div className="text-[11px] tracking-[3px] text-arc font-bold mt-4 mb-2 border-b border-hud pb-1">
      {label}
    </div>
  );
}

// ─── AlertSettings Drawer ─────────────────────────────────────────────────────

export const AlertSettings = memo(function AlertSettings() {
  const alerts       = useTerminalStore(selectAlertSettings);
  const updateAlerts = useTerminalStore(s => s.updateAlertSettings);
  const close        = useTerminalStore(s => s.setShowAlertSettings);

  const [webhookUrl,    setWebhookUrl]    = useState(alerts.webhookUrl    ?? '');
  const [webhookSecret, setWebhookSecret] = useState(alerts.webhookSecret ?? '');
  const [webhookStatus, setWebhookStatus] = useState<'idle' | 'sending' | 'ok' | 'fail'>('idle');

  const save = () => {
    // exactOptionalPropertyTypes: only set keys that have values
    const patch: Parameters<typeof updateAlerts>[0] = {};
    if (webhookUrl.trim())    patch.webhookUrl    = webhookUrl.trim();
    if (webhookSecret.trim()) patch.webhookSecret = webhookSecret.trim();
    updateAlerts(patch);
  };

  const testWebhook = async () => {
    const url = webhookUrl.trim();
    if (!url) return;

    setWebhookStatus('sending');
    try {
      const body = JSON.stringify({
        event:   'test',
        message: 'ARC Terminal webhook test',
        sentAt:  Date.now(),
      });
      const res = await fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal:  AbortSignal.timeout(8_000),
      });
      setWebhookStatus(res.ok ? 'ok' : 'fail');
    } catch {
      setWebhookStatus('fail');
    }
    setTimeout(() => setWebhookStatus('idle'), 3000);
  };

  const requestNotifPermission = async () => {
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      updateAlerts({ notificationsEnabled: true });
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex justify-end pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 pointer-events-auto"
        onClick={() => close(false)}
      />

      {/* Drawer */}
      <div className="relative w-72 h-full bg-[#040d1a] border-l border-arc shadow-2xl pointer-events-auto flex flex-col">
        {/* Header */}
        <div className="panel-header flex-shrink-0">
          <span className="text-[11px] tracking-[3px] text-arc font-bold">ALERT CONFIG</span>
          <button
            onClick={() => close(false)}
            className="ml-auto text-muted hover:text-bright font-mono text-[12px] leading-none"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">

          {/* ── Sound Alerts ── */}
          <SectionHead label="SOUND ALERTS" />
          <Toggle value={alerts.soundEnabled} onChange={v => updateAlerts({ soundEnabled: v })} label="ENABLE SOUNDS" />
          <Toggle value={alerts.alertOnBuy}   onChange={v => updateAlerts({ alertOnBuy: v })}   label="BUY SIGNAL" />
          <Toggle value={alerts.alertOnSell}  onChange={v => updateAlerts({ alertOnSell: v })}  label="SELL SIGNAL" />

          <div className="flex gap-2 mt-2">
            <button
              onClick={testBuySound}
              className="flex-1 py-1 border border-buy/40 text-buy font-mono text-[11px] hover:bg-buy/10 transition-colors"
            >
              TEST BUY ▶
            </button>
            <button
              onClick={testSellSound}
              className="flex-1 py-1 border border-sell/40 text-sell font-mono text-[11px] hover:bg-sell/10 transition-colors"
            >
              TEST SELL ▶
            </button>
          </div>

          {/* ── Browser Notifications ── */}
          <SectionHead label="NOTIFICATIONS" />
          <Toggle
            value={alerts.notificationsEnabled}
            onChange={v => {
              if (v && Notification.permission !== 'granted') {
                requestNotifPermission();
              } else {
                updateAlerts({ notificationsEnabled: v });
              }
            }}
            label="BROWSER NOTIFICATIONS"
          />
          {Notification.permission === 'denied' && (
            <p className="font-mono text-[11px] text-sell mt-1">
              Notifications blocked by browser. Enable in site settings.
            </p>
          )}

          {/* ── Webhook ── */}
          <SectionHead label="OUTBOUND WEBHOOK" />
          <div className="mb-1.5">
            <label className="font-mono text-[11px] text-muted block mb-1">WEBHOOK URL</label>
            <input
              type="url"
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              onBlur={save}
              placeholder="https://your-endpoint.com/hook"
              className="w-full bg-arc-dim border border-hud text-bright font-mono text-[11px] px-2 py-1.5 outline-none focus:border-arc placeholder-muted"
            />
          </div>
          <div className="mb-2">
            <label className="font-mono text-[11px] text-muted block mb-1">WEBHOOK SECRET (HMAC-SHA256)</label>
            <input
              type="password"
              value={webhookSecret}
              onChange={e => setWebhookSecret(e.target.value)}
              onBlur={save}
              placeholder="Optional signing secret"
              className="w-full bg-arc-dim border border-hud text-bright font-mono text-[11px] px-2 py-1.5 outline-none focus:border-arc placeholder-muted"
            />
          </div>

          <button
            onClick={testWebhook}
            disabled={!webhookUrl.trim() || webhookStatus === 'sending'}
            className={`w-full py-1.5 font-mono text-[11px] tracking-[2px] border transition-colors ${
              webhookStatus === 'ok'
                ? 'border-buy text-buy'
                : webhookStatus === 'fail'
                ? 'border-sell text-sell'
                : 'border-arc text-arc hover:bg-arc/10 disabled:opacity-40 disabled:cursor-not-allowed'
            }`}
          >
            {webhookStatus === 'sending' ? 'SENDING…'
              : webhookStatus === 'ok'   ? '✓ DELIVERED'
              : webhookStatus === 'fail' ? '✗ FAILED'
              : 'TEST WEBHOOK'}
          </button>

          <div className="mt-3 p-2 border border-hud bg-arc-dim">
            <p className="font-mono text-[11px] text-muted leading-relaxed">
              Signals are delivered as POST requests with a signed JSON payload.
              Verify authenticity using the <span className="text-arc">X-ARC-Signature</span> header (sha256=).
            </p>
          </div>

        </div>
      </div>
    </div>
  );
});
